import { type FC } from "react";

import { ValueError } from "@moment/design-system/ValueError";

import { formatDate } from "~/components/inspector/format/formatDate";
import { formatError } from "~/components/inspector/format/formatError";
import { formatRegExp } from "~/components/inspector/format/formatRegExp";
import { formatSymbol } from "~/components/inspector/format/formatSymbol";
import { AtomValue } from "~/components/inspector/values/AtomValue";
import { ForbiddenValue } from "~/components/inspector/values/ForbiddenValue";
import { FunctionValue } from "~/components/inspector/values/FunctionValue";
import { NumberValue } from "~/components/inspector/values/NumberValue";
import { StringValue } from "~/components/inspector/values/StringValue";

import { type TypeInfoNonExpandable } from "../traverseValue/TypeTagInfo";

/**
 * Displays the value for a non-expandable value in the object inspector. Non-expandable values
 * include the usual JavaScript primitives (string, number, etc.) as well as some of the more exotic
 * values, such as "forbidden" fields which are inaccessible for security reasons.
 */
export const NonExpandableValue: FC<{ typeInfo: TypeInfoNonExpandable }> = ({ typeInfo }) => {
	switch (typeInfo.typeTag) {
		case "boolean":
		case "undefined":
		case "null":
			return <AtomValue>{String(typeInfo.value)}</AtomValue>;
		case "number":
			return (
				<NumberValue>
					{typeInfo.value === 0 && 1 / typeInfo.value < 0 ? "-0" : typeInfo.value + ""}
				</NumberValue>
			);
		case "bigint":
			return <NumberValue>{typeInfo.value + "n"}</NumberValue>;
		case "symbol":
			return <NumberValue>{formatSymbol(typeInfo.value)}</NumberValue>;
		case "function":
			return <FunctionValue f={typeInfo.value} />;
		case "string":
			// FIXME: shallow
			return <StringValue string={typeInfo.value} shallow />;
		case "Date":
			return <NumberValue>{formatDate(typeInfo.value)}</NumberValue>;
		case "FORBIDDEN":
			return <ForbiddenValue />;
		case "RegExp":
			return <NumberValue>{formatRegExp(typeInfo.value)}</NumberValue>;
		case "Error":
		case "DOMException":
			return (
				<ValueError message={formatError(typeInfo.value)} originalError={typeInfo.value} />
			);
	}
};
